import { useRef, useEffect, useState, memo, useCallback } from 'react'
import { createPortal } from 'react-dom'
import useClickOutside from '../../hooks/use-clickoutside'
import { zIndex } from '../../constants'
import { Desktop, Mobile } from '../media'
import CloseX from '../svg/close-x'
import FlatX from '../svg/flat-x'
import { Button } from '../button'

const Modal = memo(
  ({ top, closeOnClickOutside = true, hideCloseBtn = false, name, onClose, children, ...rest }) => {
    // Avoid Warning: 'Expected server HTML to contain a matching <div> in <div>.'
    // portal can't be created during SSR
    const [hydrated, setHydrated] = useState(false)
    const modalRef = useRef()
    const mouseDownTarget = useRef()
    const containerRef = useRef()

    useEffect(() => {
      containerRef.current = document.createElement('div')

      document.body.appendChild(containerRef.current)
      setHydrated(true)

      return () => {
        document.body.removeChild(containerRef.current)
      }
    }, [])

    const handleClose = useCallback(() => {
      onClose(name)
    }, [name, onClose])

    useClickOutside(
      modalRef,
      handleClose,
      !closeOnClickOutside || (typeof window !== 'undefined' && window.innerWidth < 1024),
      mouseDownTarget,
    )

    const handleMouseDown = (event) => {
      event.persist()
      mouseDownTarget.current = event.target
    }

    // Uses separate useEffect to prevent re-creation of modal parent
    // on every onClose reference change, even tho it's expected to be memoized
    useEffect(() => {
      const escapeListener = (event) => {
        if (event.key === 'Escape') onClose(name)
      }

      document.addEventListener('keydown', escapeListener)

      return () => document.removeEventListener('keydown', escapeListener)
    }, [name, onClose])

    const Component = (
      <div className='backdrop' onMouseDown={handleMouseDown}>
        <div ref={modalRef} className='modal' role='dialog' aria-modal='true'>
          <section className='content'>{children}</section>

          {!hideCloseBtn && (
            <button className='flatBtn close' onClick={() => onClose(name)} aria-label='close'>
              <Desktop>
                <CloseX />
              </Desktop>
              <Mobile>
                <FlatX />
              </Mobile>
            </button>
          )}
        </div>

        <style jsx>{`
          .backdrop {
            position: fixed;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: rgba(0, 0, 0, 0.47);
            z-index: ${zIndex.Modal};
          }

          .modal {
            position: absolute;
            left: 50vw;
            top: ${top ?? '50vh'};
            transform: translateX(calc(-50%)) translateY(calc(-50%));

            :global(.wrapper) {
              background: var(--primary-bg-color);
            }

            .close {
              position: absolute;
              top: -3rem;
              right: -3rem;
              width: 2.8rem;
              height: 2.8rem;
              background: #868786;
              border-radius: 50%;

              :global(svg) {
                width: 100%;
                height: 100%;
                fill: #fff;
              }
            }

            .content {
              position: relative;
              overflow: auto;
              max-height: 100vh;
              border-radius: 1rem;
            }
          }

          @media (max-width: 1024px) {
            .backdrop {
              top: 0;
              background: #fff;

              .modal {
                position: absolute;
                left: 0;
                right: 0;
                top: 5.5rem;
                width: 100%;
                height: calc(100% - 5.5rem);
                transform: unset;
                background: var(--primary-bg-color);

                .close {
                  top: 1rem;
                  right: 1rem;
                  width: 3rem;
                  height: 3rem;
                  padding: 1rem;
                  background: var(--secondary-button-bg);
                  border-radius: 50%;

                  :global(svg) {
                    fill: var(--common-white);
                  }
                }

                .content {
                  height: 100%;
                  border-radius: 0;
                }
              }
            }
          }
        `}</style>
      </div>
    )

    if (!hydrated) return null

    return createPortal(Component, containerRef.current)
  },
)

export default Modal
